import { closeAllForms } from '../utils/closeallforms';
import { openForm } from '../utils/openform';
import { updateReferrerFields } from '../utils/updatereferrerfields';
import { getCSRFToken } from '../utils/getCSRFToken';
import { postJSONWithCSRFRetry } from '../utils/fetchWithCSRFRetry';
import { formatDateISO, addDays, parseDate } from '../utils/dateUtils';
import { deepEqual } from '../utils/deepEqual';
import { end } from '@popperjs/core';
import { safeJsonParse } from '../utils/authErrorHandler';

export default function registerUpdateBookingForm() {
  // UPDATE BOOKING
  window.Alpine.store('editBookingItemForm', {
    bookingItemId: '',
    bookingId: '',
    unitId: '',
    externalItemId: null,
    bookingSource: null,
    bookingSourceDisplay: null,

    peopleAsOption: false,
    peoplePerUnit: 0, // Store the people per unit from RateSet
    amountPeopleOptions: [], // Store the generated options for amount_people
    selectedPeople: 1, // Default selected option

    mainGuest: '',
    startDate: '',
    endDate: '',
    product: 'Accommodation',
    soldAs: null,
    initialSoldAs: null,
    initialUnitId: null,
    initialAdults: 0,
    initialChildren: 0,
    initialPersonType: '',
    initialFlagColor: '',
    unitType: '',
    dateSelectType: 'range',
    fixedDateRange: '',
    showFixedDateRange: false,
    unavailableDates: [],
    initialStartDate: '',
    initialEndDate: '',
    price: 0,
    originalPrice: 0, // Store original price for comparison
    priceUpdated: false,
    priceLoading: false, // True when price is being calculated
    // Discount information
    discountAmount: 0,
    appliedDiscounts: [],
    // Complete reprice toggle
    completeReprice: false, // When true, recalculate entire booking with current rates and discounts
    repriceAmount: null, // Cached reprice amount when toggle is ON
    preservedAmount: null, // Cached preserved price amount when toggle is OFF
    // Lock price toggle — keep final price and discounts; only redistribute per-day amounts
    priceLocked: false,
    lockedFinalPrice: 0,
    lockedDiscountAmount: 0,
    lockedAppliedDiscounts: [],
    // Track original values for warning messages in preserve mode
    initialRateSetId: null,
    initialPeople: 1,
    peopleInfluencePrice: false, // Whether people count affects pricing for current rate set
    // Warning message for preserve mode when pricing-affecting changes are made
    preserveModeWarning: null,
    // Price change modal (legacy - keeping for backwards compatibility)
    showPriceChangeModal: false,
    priceChangeAction: null, // 'abort', 'update', or 'preserve'
    pendingSubmitEvent: null, // Store the event for later submission
    flatpickrInstance: null,
    flatpickrInstances: {},
    categorizedDates: {
      start: [],
      single: [],
      middle: [],
      end: [],
    },
    minMaxDays: {}, // Min/max days constraints per date
    packageClosedToStartDates: [], // Package-only blocked start dates (admin-safe)
    packages: [], // Package info for dynamic availability checking
    usePersonTypes: false,
    selectedAdults: 0,
    selectedChildren: 0,
    adultOptions: [],
    childrenOptions: [],
    maxPersons: 0,
    personType: '',

    // Tour duration selection
    tourDuration: 'single', // Default to single day for tours
    showTourDurationToggle: false, // Only show when rate set allows range

    // Custom fields
    selectedRateSet: null,
    customFields: {},
    // Use an object instead of array for customFieldsPerPerson to avoid undefined access issues
    // Keys are person indices (0, 1, 2, etc.), values are objects with field values
    customFieldsPerPerson: {},

    // Flag color for booking item (hex or '')
    flagColor: '',
    isInitializing: false, // Backdrop visible while hydrating form data
    showLoadingIndicator: false, // Delayed spinner/text on top of backdrop
    loadingIndicatorShownAt: null,
    loadingIndicatorShowTimer: null,
    loadingIndicatorHideTimer: null,
    initializationTimeoutTimer: null,

    normalizeAppliedDiscount(discount) {
      if (!discount || typeof discount !== 'object') {
        return null;
      }
      const id = discount.discount_id || discount.id;
      return {
        ...discount,
        id: id != null ? String(id) : undefined,
        name: discount.name || 'Discount',
      };
    },

    normalizeAppliedDiscounts(discounts) {
      if (!Array.isArray(discounts)) {
        return [];
      }
      return discounts
        .map((discount) => this.normalizeAppliedDiscount(discount))
        .filter((discount) => discount && discount.id);
    },

    clearInitializationTimers() {
      if (this.loadingIndicatorShowTimer) {
        clearTimeout(this.loadingIndicatorShowTimer);
        this.loadingIndicatorShowTimer = null;
      }
      if (this.loadingIndicatorHideTimer) {
        clearTimeout(this.loadingIndicatorHideTimer);
        this.loadingIndicatorHideTimer = null;
      }
      if (this.initializationTimeoutTimer) {
        clearTimeout(this.initializationTimeoutTimer);
        this.initializationTimeoutTimer = null;
      }
    },

    hideInitializingState() {
      this.isInitializing = false;
      this.showLoadingIndicator = false;
      this.loadingIndicatorShownAt = null;
    },

    startInitializing() {
      this.clearInitializationTimers();
      this.isInitializing = true;
      this.showLoadingIndicator = false;
      this.loadingIndicatorShownAt = null;
      this.loadingIndicatorShowTimer = setTimeout(() => {
        this.showLoadingIndicator = true;
        this.loadingIndicatorShownAt = Date.now();
        this.loadingIndicatorShowTimer = null;
      }, 1000);
      this.initializationTimeoutTimer = setTimeout(() => {
        console.warn('Edit booking form initialization timed out');
        this.stopInitializing();
        const toastStore = Alpine.store('toastStore');
        if (toastStore && toastStore.addToast) {
          toastStore.addToast(
            'Loading booking details took too long. Please try again.',
            'warning',
          );
        }
      }, 15000);
    },
    stopInitializing() {
      if (this.loadingIndicatorShowTimer) {
        clearTimeout(this.loadingIndicatorShowTimer);
        this.loadingIndicatorShowTimer = null;
      }
      if (this.initializationTimeoutTimer) {
        clearTimeout(this.initializationTimeoutTimer);
        this.initializationTimeoutTimer = null;
      }

      if (!this.showLoadingIndicator) {
        if (this.loadingIndicatorHideTimer) {
          clearTimeout(this.loadingIndicatorHideTimer);
          this.loadingIndicatorHideTimer = null;
        }
        this.hideInitializingState();
        return;
      }

      const elapsed = Date.now() - (this.loadingIndicatorShownAt || 0);
      const remainingVisibleMs = Math.max(0, 1000 - elapsed);
      if (remainingVisibleMs === 0) {
        this.hideInitializingState();
        return;
      }

      if (this.loadingIndicatorHideTimer) {
        clearTimeout(this.loadingIndicatorHideTimer);
      }
      this.loadingIndicatorHideTimer = setTimeout(() => {
        this.hideInitializingState();
        this.loadingIndicatorHideTimer = null;
      }, remainingVisibleMs);
    },
    hideInitializingOverlayImmediate() {
      this.clearInitializationTimers();
      this.hideInitializingState();
    },

    // Safe getter for per-person custom fields - ALWAYS returns an object, never undefined
    // This prevents "Cannot read properties of undefined" errors during Alpine's reactive re-rendering
    getPersonFieldsObj(personIndex) {
      if (!this.customFieldsPerPerson[personIndex]) {
        this.customFieldsPerPerson[personIndex] = {};
      }
      return this.customFieldsPerPerson[personIndex];
    },
    getPersonLabel(count = 2, lowercase = false) {
      const singular = (
        this.selectedRateSet?.person_label_singular || 'Person'
      ).trim();
      const plural = (
        this.selectedRateSet?.person_label_plural || 'People'
      ).trim();
      const label = Number(count) === 1 ? singular : plural;
      return lowercase ? label.toLowerCase() : label;
    },
    getPersonFieldLabel() {
      return this.getPersonLabel(2);
    },
    showPersonIcon() {
      const rs = this.selectedRateSet;
      if (!rs) return true;
      const raw = rs.person_label_singular;
      if (raw == null || raw === '') return true;
      const s = String(raw).trim();
      return !s || s.toLowerCase() === 'person';
    },

    validationErrors: {
      peopleUnits: '',
      startEndDate: '',
      personType: '',
      channexComplexChange: '',
      // add other validation fields here as needed
    },

    init() {
      document.addEventListener('htmx:afterSwap', (event) => {
        const accommodationContainer = document.querySelector(
          '#accommodation-unit-id-container',
        );
        const tourContainer = document.querySelector('#tour-unit-id-container');

        if (event.detail.target.id === 'accommodation-unit-id-container') {
          const selectedUnitId = accommodationContainer.querySelector(
            '#accommodation_unit_id',
          )?.value;
          this.unitId = selectedUnitId;
          console.log(
            'Updated unitId for Accommodation in store:',
            selectedUnitId,
          );
        }

        if (event.detail.target.id === 'tour-unit-id-container') {
          const selectedUnitId =
            tourContainer.querySelector('#tour_unit_id')?.value;
          this.unitId = selectedUnitId;
          console.log('Updated unitId for Tour in store:', selectedUnitId);
        }
      });
    },

    async handleUpdateBookingItemClick(e) {
      console.log('*** HANDLING UPDATE BOOKING ITEM CLICK ***');
      closeAllForms();
      this.resetStore();
      const toastStore = Alpine.store('toastStore');
      // Retrieve calendar ID from the calendar container
      const calendarContainer = document.querySelector('.calendar-container'); // Select the calendar container

      if (calendarContainer) {
        this.calendarId = calendarContainer.getAttribute('data-calendar-id'); // Retrieve calendar ID
      } else {
        console.error('Calendar container not found');
      }

      // Find the booking item element (it might be a child element that was clicked)
      let bookingItemElement = e.target;
      while (
        bookingItemElement &&
        !bookingItemElement.hasAttribute('data-booking-item-id')
      ) {
        bookingItemElement = bookingItemElement.parentElement;
      }

      if (!bookingItemElement) {
        console.error('Booking item element not found');
        return;
      }

      // Get booking item ID
      const uuid = bookingItemElement.getAttribute('data-booking-item-id');
      this.bookingItemId = uuid;

      if (bookingItemElement.classList.contains('calendar-tour-booking-item')) {
        this.product = 'Tour';
      } else {
        this.product = 'Accommodation';
      }

      const formIdToUpdate =
        this.product === 'Accommodation'
          ? 'updateAccommodationForm'
          : 'updateTourForm';

      await this.renderForm();
      openForm(
        formIdToUpdate,
        'open',
        { booking_item_id: this.bookingItemId },
        null,
        null,
      );
      document.body.classList.add('slideform-open'); // Prevent background scrolling while form is open
      this.startInitializing();

      try {
        // Fetch booking item data from API - this is much lighter than storing all data in HTML attributes!
        const bookingItemData = await this.fetchBookingItemData(uuid);
        if (!bookingItemData) {
          throw new Error('Failed to load booking item data.');
        }

        // Populate store from API data
        await this.populateStoreFromBookingItem(bookingItemData);

        // Get some additional data from booking item element for backward compatibility
        const unitsUpdateUrl = bookingItemElement.getAttribute(
          'data-url-update-units',
        );
        const bookingStatus = bookingItemElement.getAttribute(
          'data-booking-status',
        );
        const isFixed = this.selectedRateSet?.is_fixed || false;

        // Convert string dates to Date objects
        let end_date_obj = new Date(this.endDate + 'T00:00:00Z');

        // Show tour duration toggle only for tours with range mode and not fixed
        this.showTourDurationToggle =
          this.product === 'Tour' &&
          this.dateSelectType === 'range' &&
          !isFixed;

        // Set tour duration based on existing booking dates
        if (this.showTourDurationToggle) {
          // Check if start and end dates are the same (single day) or different (multiple days)
          this.tourDuration =
            this.startDate === this.endDate ? 'single' : 'multi';
        }

        // Update unit queryset depending on which RateSet
        if (formIdToUpdate === 'updateAccommodationForm') {
          if (this.soldAs) {
            console.log('UPDATE ACCOMM UNIT QUERYSET!!!');
            // Use HTMX to fetch and update the accommodation units
            htmx.ajax('GET', unitsUpdateUrl, {
              values: {
                accommodation_rate_set: this.soldAs,
                selected_unit_id: this.unitId,
              },
              target: '#accommodation-unit-id-container',
            });
          }
        } else {
          if (this.soldAs) {
            console.log('UPDATE TOUR UNIT QUERYSET!!!');
            // Use HTMX to fetch and update the tour units
            htmx.ajax('GET', unitsUpdateUrl, {
              values: {
                tour_rate_set: this.soldAs,
                selected_unit_id: this.unitId,
              },
              target: '#tour-unit-id-container',
            });
          }
        }

        // If accommodation booking add one day to end_date
        let end_date = this.endDate;
        if (formIdToUpdate === 'updateAccommodationForm') {
          end_date = formatDateISO(addDays(end_date_obj, 1));
          this.endDate = end_date;
        }

        // Update initial dates
        this.updateInitialDates(isFixed, this.startDate, end_date);

        // Reset & Initialize Flatpickr (ensure it reinitializes every time the form is opened)
        const flatpickrElement = document.querySelector(
          `#${formIdToUpdate} [x-ref="datepicker"]`,
        );
        if (flatpickrElement) {
          this.resetFlatpickr(this.product, flatpickrElement);
        } else {
          console.log('Flatpickr element not found');
        }

        // Highlight other booking items that belong to the same booking
        // Get all elements representing booking items
        const tourBookingItems = document.querySelectorAll(
          '.calendar-tour-booking-item',
        );
        const accommodationBookingItems = document.querySelectorAll(
          '.calendar-accomm-booking-item',
        );
        // Concatenate the two NodeLists into an array
        const bookingItems = [
          ...tourBookingItems,
          ...accommodationBookingItems,
        ];
        // Iterate over booking items
        bookingItems.forEach((item) => {
          // Check if the booking item belongs to the same booking
          if (item.getAttribute('data-booking-id') === this.bookingId) {
            // If so, add a CSS class to highlight it
            item.style.zIndex = '3';
          }
        });
        // Add 'selected' class to the clicked item
        bookingItemElement.classList.add('selected');

        // Add booking details to fieldData
        let fieldData;
        if (formIdToUpdate === 'updateAccommodationForm') {
          fieldData = {
            booking_item_id: this.bookingItemId,
            unit_id: this.unitId,
            rate_set: this.soldAs,
            start_date: this.startDate,
            end_date: this.endDate,
          };
        } else {
          fieldData = {
            booking_item_id: this.bookingItemId,
            unit_id: this.unitId,
            rate_set: this.soldAs,
            start_date: this.startDate,
            end_date: this.endDate,
          };
        }

        // Prepare details for Full Booking link
        const fullBookingLink =
          formIdToUpdate === 'updateAccommodationForm'
            ? 'fullAccommBookingLink'
            : 'fullTourBookingLink';

        openForm(
          formIdToUpdate,
          'open',
          fieldData,
          fullBookingLink,
          this.bookingId,
          bookingStatus,
        );

        document.querySelector(
          `#${formIdToUpdate} form`,
        ).action = `/bookings_app/${this.bookingItemId}/update/`;
        updateReferrerFields();
        // Remove original dates if they are still tehre from past usage
        this.removeOriginalDates();
      } catch (error) {
        console.error('Error preparing booking item form:', error);
        this.closeForm();
        if (toastStore && toastStore.addToast) {
          toastStore.addToast(
            error.message || 'Unable to load booking item details.',
            'danger',
          );
        }
      } finally {
        this.stopInitializing();
      }
    },
    async renderForm() {
      // Render the form on the DOM
      let formContainer;
      let updateBookingItemForm;
      let url;

      if (this.product === 'Accommodation') {
        formContainer = document.getElementById(
          'update-accommodation-item-form-container',
        );
        updateBookingItemForm = document.getElementById(
          'updateAccommodationForm',
        );
        url = `/calendar/update-accommodation-item-form/${this.calendarId}/`;
      } else if (this.product === 'Tour') {
        formContainer = document.getElementById(
          'update-tour-item-form-container',
        );
        updateBookingItemForm = document.getElementById('updateTourForm');
        url = `/calendar/update-tour-item-form/${this.calendarId}/`;
      }

      if (updateBookingItemForm) {
        const hasLoadingShell =
          updateBookingItemForm.querySelector(
            '[data-loading-overlay="true"]',
          ) &&
          updateBookingItemForm.querySelector('[data-loading-content="true"]');
        if (hasLoadingShell) {
          // Form is already rendered, just open it
          return; // Exit the function early
        }
        // Backward compatibility: replace stale in-DOM form markup rendered before loading-shell fixes.
        updateBookingItemForm.remove();
      }

      // Fetch the product form from the server
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-CSRFToken': getCSRFToken(),
        },
      });

      // Use safe JSON parsing to handle auth errors gracefully
      const data = await safeJsonParse(response);
      // Update the form container with the fetched HTML
      formContainer.innerHTML = data.update_booking_item_form_html;
    },
    async preloadFormShells(calendarId) {
      const resolvedCalendarId =
        calendarId || Alpine.store('calendarStore')?.calendar_selected?.id;
      if (!resolvedCalendarId) {
        return;
      }

      const preloadTargets = [
        {
          formId: 'updateAccommodationForm',
          containerId: 'update-accommodation-item-form-container',
          url: `/calendar/update-accommodation-item-form/${resolvedCalendarId}/`,
        },
        {
          formId: 'updateTourForm',
          containerId: 'update-tour-item-form-container',
          url: `/calendar/update-tour-item-form/${resolvedCalendarId}/`,
        },
      ];

      await Promise.all(
        preloadTargets.map(async ({ formId, containerId, url }) => {
          const existingForm = document.getElementById(formId);
          if (
            existingForm &&
            existingForm.querySelector('[data-loading-overlay="true"]') &&
            existingForm.querySelector('[data-loading-content="true"]')
          ) {
            return;
          }

          const container = document.getElementById(containerId);
          if (!container) {
            return;
          }

          try {
            const response = await fetch(url, {
              method: 'GET',
              headers: {
                'X-CSRFToken': getCSRFToken(),
              },
            });
            const data = await safeJsonParse(response);
            container.innerHTML = data.update_booking_item_form_html;
          } catch (error) {
            console.warn(`Failed preloading ${formId} shell`, error);
          }
        }),
      );
    },

    async fetchBookingItemData(bookingItemId) {
      console.log('***** FETCHING BOOKING ITEM DATA FROM API *****');
      try {
        // OPTIMIZED: Use new lightweight endpoint for on-demand booking item data
        const response = await fetch(
          `/calendar/api/booking-item/${bookingItemId}/`,
        );
        const data = await safeJsonParse(response);
        console.log('Fetched booking item data:', data);
        return data;
      } catch (error) {
        console.error('Error fetching booking item data:', error);
        return null;
      }
    },

    async populateStoreFromBookingItem(bookingItemData) {
      console.log('***** POPULATING STORE FROM BOOKING ITEM DATA *****');

      // Basic booking info
      const mainGuest = bookingItemData.booking?.main_guest;
      this.mainGuest = mainGuest
        ? `${mainGuest.first_name || ''} ${mainGuest.last_name || ''}`.trim()
        : '';
      this.bookingId = bookingItemData.booking?.id || '';
      this.externalItemId = bookingItemData.external_item_id || null;
      this.bookingSource = bookingItemData.booking?.booking_source || null;
      this.bookingSourceDisplay =
        bookingItemData.booking?.booking_source_display || null;
      this.price = parseFloat(bookingItemData.final_price) || 0;
      this.originalPrice = this.price;
      this.discountAmount = parseFloat(bookingItemData.discount_amount) || 0;
      this.appliedDiscounts = this.normalizeAppliedDiscounts(
        bookingItemData.applied_discounts,
      );
      this.lockedFinalPrice = this.price;
      this.lockedDiscountAmount = this.discountAmount;
      this.lockedAppliedDiscounts = this.appliedDiscounts.map((d) => ({
        ...d,
      }));
      this.priceLocked = false;

      // Unit info
      // accommodation_unit and tour_unit are serialized as IDs (UUIDs), not objects
      this.unitId =
        bookingItemData.accommodation_unit || bookingItemData.tour_unit || '';
      this.initialUnitId = this.unitId; // Store initial unit for comparison

      // Rate set info
      const rateSet = bookingItemData.rate_set;
      this.soldAs = rateSet?.id || null;
      this.initialSoldAs = rateSet?.id || null; // Store initial rate set for comparison
      this.initialRateSetId = rateSet?.id || null; // For preserve mode warning
      const effectiveCustomFields =
        bookingItemData.effective_custom_fields || [];
      this.selectedRateSet = rateSet
        ? {
            ...rateSet,
            // For package components, edit form should render effective fields
            // (package-targeted + listing assignments with package precedence).
            active_custom_fields: effectiveCustomFields,
          }
        : null;
      this.unitType = rateSet?.unit_type || '';
      this.peoplePerUnit = rateSet?.people_per_unit || 0;
      this.peopleAsOption = this.peoplePerUnit > 0;
      this.dateSelectType = rateSet?.date_select || 'range';
      this.peopleInfluencePrice = rateSet?.people_influence_price || false;

      // Person types and counts
      this.usePersonTypes = rateSet?.use_person_types || false;
      this.selectedPeople = bookingItemData.amount_people || 1;
      this.initialPeople = bookingItemData.amount_people || 1; // For preserve mode warning
      this.selectedAdults = bookingItemData.amount_adults || 0;
      this.selectedChildren = bookingItemData.amount_children || 0;
      this.personType = bookingItemData.person_type || '';
      this.initialAdults = this.selectedAdults;
      this.initialChildren = this.selectedChildren;
      this.initialPersonType = this.personType || '';

      // Dates
      this.startDate = bookingItemData.start_date;
      this.initialStartDate = bookingItemData.start_date;
      this.endDate = bookingItemData.end_date;
      this.initialEndDate = bookingItemData.end_date;

      // Flag color
      this.flagColor = bookingItemData.flag_color || '';
      this.initialFlagColor = this.flagColor || '';

      // Custom fields - the key part!
      this.customFields = bookingItemData.custom_fields || {};

      // Convert array from server to object for consistent access pattern
      const serverPerPersonData =
        bookingItemData.custom_fields_per_person || [];
      this.customFieldsPerPerson = {};
      serverPerPersonData.forEach((personData, index) => {
        this.customFieldsPerPerson[index] = personData || {};
      });

      // Fallback: If we have per-person data but counts are 0, derive counts from the data
      const perPersonValues = Object.values(this.customFieldsPerPerson);
      if (
        perPersonValues.length > 0 &&
        this.selectedAdults === 0 &&
        this.selectedChildren === 0
      ) {
        const adults = perPersonValues.filter(
          (p) => p._person_type === 'Adult' || p._person_type === 'ADULT',
        );
        const children = perPersonValues.filter(
          (p) => p._person_type === 'Child' || p._person_type === 'CHILD',
        );

        if (adults.length > 0) this.selectedAdults = adults.length;
        if (children.length > 0) this.selectedChildren = children.length;

        console.log('Derived person counts from custom field data:', {
          adults: this.selectedAdults,
          children: this.selectedChildren,
        });
      }

      console.log('Loaded custom fields:', this.customFields);
      console.log(
        'Loaded per-person custom fields:',
        this.customFieldsPerPerson,
      );
      console.log(
        'Per-person object details:',
        JSON.stringify(this.customFieldsPerPerson, null, 2),
      );

      // Determine product type (accommodation_unit and tour_unit are IDs, not objects)
      this.product = bookingItemData.accommodation_unit
        ? 'Accommodation'
        : 'Tour';

      // Generate options
      this.generateAmountPeopleOptions();
      this.resetPersonTypeOptions();

      console.log('Store populated successfully');
    },

    updateSelectedRate() {
      console.log('***** UPDATE SELECTED RATE *****');

      // Get currently selected RateSet from the dropdown directly
      // This ensures we get the current value, not the potentially stale soldAs value
      const rateSetSelector =
        this.product === 'Accommodation'
          ? 'select[name="accommodation_rate_set"]'
          : 'select[name="tour_rate_set"]';
      const rateSetDropdown = document.querySelector(rateSetSelector);
      const rateSetId = rateSetDropdown ? rateSetDropdown.value : this.soldAs;

      console.log('Rate set ID from dropdown:', rateSetId);
      console.log('Rate set ID from soldAs:', this.soldAs);

      // Update soldAs with the current value
      this.soldAs = rateSetId;

      // Fetch the selected RateSet data from the server
      if (rateSetId) {
        fetch(`/rates/rate-set/${rateSetId}/`)
          .then(async (response) => {
            const data = await safeJsonParse(response);
            return data;
          })
          .then((selectedRateSet) => {
            // Function to handle HTMX update and return a promise
            const updateUnits = () => {
              return new Promise((resolve, reject) => {
                const htmxUpdateCallback = () => {
                  document.removeEventListener(
                    'htmx:afterSwap',
                    htmxUpdateCallback,
                  );
                  resolve();
                };

                document.addEventListener('htmx:afterSwap', htmxUpdateCallback);

                // Update unit queryset depending on which RateSet
                // Pass dates to enable optimal unit selection based on availability
                if (this.product === 'Accommodation') {
                  htmx.ajax(
                    'GET',
                    '/bookings_app/update-accommodation-units-field/',
                    {
                      values: {
                        accommodation_rate_set: rateSetId,
                        selected_unit_id: this.unitId,
                        start_date: this.initialStartDate,
                        end_date: this.initialEndDate,
                      },
                      target: '#accommodation-unit-id-container',
                      headers: {
                        'HX-Trigger': 'unit-updated',
                      },
                    },
                  );
                } else {
                  htmx.ajax('GET', '/bookings_app/update-tour-units-field/', {
                    values: {
                      tour_rate_set: rateSetId,
                      selected_unit_id: this.unitId,
                      start_date: this.initialStartDate,
                      end_date: this.initialEndDate,
                    },
                    target: '#tour-unit-id-container',
                    headers: {
                      'HX-Trigger': 'unit-updated',
                    },
                  });
                }
              });
            };

            // Update units and then handle rate change
            updateUnits()
              .then(() => {
                this.handleRateChange(selectedRateSet);
              })
              .catch((error) => console.error('Error updating units:', error));
          })
          .catch((error) => console.error('Error fetching rate set:', error));
      }
    },
    async handleRateChange(selectedRateSet) {
      console.log('***** HANDLE RATE CHANGE *****');
      console.log('Selected RateSet:', selectedRateSet);
      console.log('Previous State:', {
        usePersonTypes: this.usePersonTypes,
        peopleAsOption: this.peopleAsOption,
        unitType: this.unitType,
      });

      // Explicitly set all relevant properties from the rate set first
      this.dateSelectType = selectedRateSet.date_select;
      this.peoplePerUnit = selectedRateSet.people_per_unit;
      this.unitType = selectedRateSet.unit_type;

      // Important: Ensure boolean values with explicit conversion
      this.peopleAsOption = Boolean(selectedRateSet.people_as_option);
      this.usePersonTypes = Boolean(selectedRateSet.use_person_types);
      // Track if people count affects pricing for this rate set (used for preserve mode warning)
      this.peopleInfluencePrice = Boolean(
        selectedRateSet.people_influence_price,
      );

      // Always set units to 1 when changing rates
      this.units = 1;

      console.log('Rate Set Properties:', {
        people_as_option: selectedRateSet.people_as_option,
        use_person_types: selectedRateSet.use_person_types,
        unit_type: selectedRateSet.unit_type,
        peoplePerUnit: this.peoplePerUnit,
        units: this.units,
      });

      // Smart people count preservation: Store current values before resetting
      const previousPeople = this.selectedPeople;
      const previousAdults = this.selectedAdults;
      const previousChildren = this.selectedChildren;

      // Helper to check if unit type is a person type (Person, Adult, or Child)
      const isPersonUnitType =
        this.unitType === 'Person' ||
        this.unitType === 'Adult' ||
        this.unitType === 'Child';

      // Reset person-related fields based on unit type and usePersonTypes
      if (isPersonUnitType) {
        this.personType = ''; // Reset to default
        this.selectedPeople = 1; // Always 1 for Person/Adult/Child type
        this.generatePersonTypeOptions(1); // For Person/Adult/Child type
        // For Person types with usePersonTypes, default to 1 adult
        if (this.usePersonTypes) {
          this.selectedAdults = 1;
          this.selectedChildren = 0;
          this.units = 1;
        } else {
          this.selectedAdults = 0;
          this.selectedChildren = 0;
          this.units = 1;
        }
      } else if (this.usePersonTypes) {
        this.personType = '';
        this.generatePersonTypeOptions(); // Now units should be properly set

        // Check if previous adults/children combo is valid in new options
        if (
          this.adultOptions.includes(previousAdults) &&
          this.childrenOptions.includes(previousChildren)
        ) {
          // Preserve previous selection
          this.selectedAdults = previousAdults;
          this.selectedChildren = previousChildren;
          console.log(
            'Preserved person types - Adults:',
            this.selectedAdults,
            'Children:',
            this.selectedChildren,
          );
        } else {
          // Use defaults: 1 adult, 0 children
          this.selectedAdults = 1;
          this.selectedChildren = 0;
          console.log(
            'Person types reset - using defaults: 1 adult, 0 children',
          );
        }
      } else if (this.peopleAsOption) {
        this.selectedPeople = 1;
        this.generateAmountPeopleOptions();

        // Check if previous people count is valid in new options
        if (this.amountPeopleOptions.includes(previousPeople)) {
          // Preserve previous selection
          this.selectedPeople = previousPeople;
          console.log('Preserved people count:', this.selectedPeople);
        } else {
          // Use default (already set to 1 above)
          console.log(
            'People count reset - previous value not valid in new rate set',
          );
        }
      } else {
        this.selectedPeople = 0;
      }

      // Store current dates before checking availability
      const currentStartDate = this.startDate;
      const currentEndDate = this.endDate;

      // Check if original dates are available before clearing
      const datesAvailable = await this.checkDateAvailability(
        selectedRateSet.id,
        this.unitId,
      );
      this.resetDatesAndPrice(!datesAvailable);

      if (datesAvailable) {
        // Keep the current dates
        console.log('Original dates available, preserving them');
        this.removeOriginalDates();
        // Clear any date validation errors since dates are available
        this.clearValidationError('startEndDate');

        // In PRESERVE mode: Don't recalculate price - just show warning if needed
        // In REPRICE mode: Recalculate with new rate set
        if (this.completeReprice) {
          this.updatePrice();
        } else {
          // Preserve mode - just update warning, keep original price
          this.updatePreserveModeWarning();
        }
      } else {
        console.log('Original dates NOT available, clearing dates');

        // Show validation error instead of original dates warning
        if (currentStartDate && currentEndDate) {
          this.setValidationError(
            'startEndDate',
            'Original dates are unavailable for this listing',
          );
        }
      }

      // Handle date selection type
      if (selectedRateSet.is_fixed) {
        this.showFixedDateRange = true;
        this.populateFixedDateRangeDropdown(this.product, selectedRateSet.id);
      } else {
        this.showFixedDateRange = false;
        if (!datesAvailable) {
          this.resetFlatpickrDates(selectedRateSet.id);
        }
        // Note: flatpickr will be reinitialized when the form is opened via resetFlatpickr
      }

      // Show tour duration toggle only for tours with range mode
      this.showTourDurationToggle =
        this.product === 'Tour' && selectedRateSet.date_select === 'range';

      // Reset tour duration to single when showing toggle
      if (this.showTourDurationToggle) {
        this.tourDuration = 'single';
      }

      console.log('Final State:', {
        usePersonTypes: this.usePersonTypes,
        peopleAsOption: this.peopleAsOption,
        unitType: this.unitType,
        selectedAdults: this.selectedAdults,
        selectedChildren: this.selectedChildren,
        selectedPeople: this.selectedPeople,
      });

      // Check for Channex changes (including RateSet change)
      this.checkChannexComplexChange();
    },
    async handleUnitChange() {
      console.log('handleUnitChange!!!');

      // Store current dates before checking availability
      const currentStartDate = this.startDate;
      const currentEndDate = this.endDate;

      // Check if original dates are available before clearing
      const datesAvailable = await this.checkDateAvailability(
        this.soldAs,
        this.unitId,
      );
      this.resetDatesAndPrice(!datesAvailable);

      if (datesAvailable) {
        // Keep the current dates
        console.log('Original dates available, preserving them');
        // Clear any date validation errors since dates are available
        this.clearValidationError('startEndDate');

        if (this.showFixedDateRange === true) {
          // Populate dropdown with options
          this.populateFixedDateRangeDropdown(this.product, this.soldAs);
        } else {
          // Update flatpickr availability without clearing dates
          const instance = this.flatpickrInstances[this.product];
          if (instance) {
            const currentYear = instance.currentYear;
            const currentMonth = instance.currentMonth + 1;
            this.fetchUnavailableDates(
              currentYear.toString(),
              currentMonth.toString(),
              instance,
            );
          }
        }

        // In PRESERVE mode: Don't recalculate price - just show warning if needed
        // In REPRICE mode: Recalculate with new unit
        if (this.completeReprice) {
          this.updatePrice();
        } else {
          // Preserve mode - just update warning, keep original price
          this.updatePreserveModeWarning();
        }
      } else {
        console.log('Original dates NOT available, clearing dates');

        // Show validation error instead of original dates warning
        if (currentStartDate && currentEndDate) {
          this.setValidationError(
            'startEndDate',
            'Original dates are unavailable for this unit',
          );
        }

        if (this.showFixedDateRange === true) {
          this.populateFixedDateRangeDropdown(this.product, this.soldAs);
        } else {
          this.resetFlatpickrDates(this.soldAs);
        }
      }

      // Check for Channex changes (including Unit change)
      this.checkChannexComplexChange();
    },
    handleAmountPeopleChange() {
      console.log('FUNCTION CALLED: handleAmountPeopleChange');
      console.log(this); // Check what 'this' refers to
      if (this.showFixedDateRange === true) {
        // Populate dropdown with options
        this.populateFixedDateRangeDropdown(this.product, this.soldAs);
      }

      // In PRESERVE mode: Don't recalculate price - just show warning if needed
      // In REPRICE mode: Recalculate with new people count
      if (this.completeReprice) {
        this.updatePrice();
      } else {
        // Preserve mode - just update warning, keep original price
        this.updatePreserveModeWarning();
      }
    },
    generateAmountPeopleOptions() {
      console.log('FUNCTION CALLED: generateAmountPeopleOptions');
      console.log('peoplePerUnit:', this.peoplePerUnit);

      const maxPeople = this.peoplePerUnit;
      console.log('maxPeople:', maxPeople);

      this.amountPeopleOptions = Array.from(
        { length: maxPeople },
        (_, i) => i + 1,
      );
      console.log('amountPeopleOptions:', this.amountPeopleOptions);
    },

    populateFixedDateRangeDropdown(
      product,
      soldAsId,
      startDate = null,
      endDate = null,
    ) {
      console.log('Populating Fixed Date Range Dropdown...');
      const csrfToken = getCSRFToken();

      // Normalize date formats (DD/MM/YYYY to YYYY-MM-DD) for comparison
      const normalizeDateFormat = (dateStr) => {
        if (!dateStr) return null;
        const [day, month, year] = dateStr.split('/');
        return `${year}-${month}-${day}`;
      };

      const normalizedStartDate = startDate
        ? normalizeDateFormat(startDate)
        : null;
      const normalizedEndDate = endDate ? normalizeDateFormat(endDate) : null;

      console.log('Normalized start date:', normalizedStartDate);
      console.log('Normalized end date:', normalizedEndDate);

      fetch('/bookings_app/fixed-date-ranges/', {
        method: 'POST',
        body: JSON.stringify({
          soldAs: soldAsId,
          unitId: this.unitId,
          selectedPeople: this.selectedPeople,
          bookingItemId: this.bookingItemId,
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      })
        .then(async (response) => {
          const data = await safeJsonParse(response);
          return data;
        })
        .then((data) => {
          console.log('Received data:', data);
          let dropdown = null;
          if (product === 'Accommodation') {
            dropdown = document.querySelector(
              '#updateAccommodationForm .fixedDateRangeDropdown',
            );
          } else {
            dropdown = document.querySelector(
              '#updateTourForm .fixedDateRangeDropdown',
            );
          }

          if (!dropdown) {
            console.error('Dropdown element not found.');
            return; // Exit if dropdown is not found
          }

          dropdown.innerHTML = ''; // Clear existing options

          // Combine available and unavailable date ranges
          const allRanges = [
            ...data.available_fixed_date_ranges.map((range) => ({
              ...range,
              available: true,
            })),
            ...data.unavailable_fixed_date_ranges.map((range) => ({
              ...range,
              available: false,
            })),
          ];

          // Sort the combined array by start date and then by end date
          allRanges.sort((a, b) => {
            const startDateComparison =
              new Date(a.date.split('/').reverse().join('-')) -
              new Date(b.date.split('/').reverse().join('-'));
            if (startDateComparison !== 0) return startDateComparison;
            return (
              new Date(a.end_date.split('/').reverse().join('-')) -
              new Date(b.end_date.split('/').reverse().join('-'))
            );
          });

          // Create and append the first disabled option
          const firstOption = document.createElement('option');
          if (allRanges.length === 0) {
            firstOption.textContent = 'No dates available at this time';
          } else {
            firstOption.textContent = 'Select date';
          }
          firstOption.style.color = 'grey';
          firstOption.disabled = true;
          firstOption.selected = true; // Make this option selected by default
          dropdown.appendChild(firstOption);

          let foundMatchingOption = false; // Track if a matching option is found

          // Append sorted date ranges to the dropdown
          allRanges.forEach((range) => {
            const option = document.createElement('option');
            option.value = `${range.date}-${range.end_date}`;

            // Check if start and end dates are the same (single day)
            const isSingleDay = range.date === range.end_date;

            if (isSingleDay) {
              // For single day tours, show only one date
              option.textContent = `${range.date}${
                range.available ? '' : ' (unavailable)'
              }`;
            } else {
              // For multi-day tours, show date range
              option.textContent = `${range.date} to ${range.end_date}${
                range.available ? '' : ' (unavailable)'
              }`;
            }

            option.setAttribute('data-start-date', range.date);
            option.setAttribute('data-end-date', range.end_date);
            if (!range.available) {
              option.disabled = true; // Disable the option if unavailable
              option.style.color = 'grey'; // Optionally style the unavailable option
            }

            // Normalize the dropdown option's dates for comparison
            const normalizedOptionStartDate = normalizeDateFormat(range.date);
            const normalizedOptionEndDate = normalizeDateFormat(range.end_date);

            // Log normalized dates for debugging
            console.log(
              'Normalized Option Start Date:',
              normalizedOptionStartDate,
            );
            console.log('Normalized Option End Date:', normalizedOptionEndDate);

            // Check if this option matches the provided startDate and endDate
            if (
              normalizedStartDate &&
              normalizedEndDate &&
              normalizedOptionStartDate === normalizedStartDate &&
              normalizedOptionEndDate === normalizedEndDate
            ) {
              console.log('UPDATING INITIAL DATE RANGE...');
              option.selected = true; // Select this option if dates match
              firstOption.selected = false; // Deselect the "Select date" option
              foundMatchingOption = true; // Set flag to indicate matching option found
            }

            dropdown.appendChild(option);
          });

          // If no matching option was found and selected, ensure the "Select date" is selected
          if (!foundMatchingOption) {
            firstOption.selected = true; // Keep "Select date" as the default selected option
          }

          // Reset the fixedDateRange to force user selection after adding the first option
          this.fixedDateRange = '';

          // Only update price if dates are actually set and we found a matching option
          // Otherwise, user needs to select dates first
          if (foundMatchingOption && this.startDate && this.endDate) {
            this.updatePrice();
          }
        })
        .catch((error) =>
          console.error('Error fetching fixed date ranges:', error),
        );
    },

    fixedDateSelection() {
      let dropdown = null;
      if (this.product === 'Accommodation') {
        dropdown = document.querySelector(
          ' #updateAccommodationForm .fixedDateRangeDropdown',
        );
      } else {
        dropdown = document.querySelector(
          '#updateTourForm .fixedDateRangeDropdown',
        );
      }
      const selectedOption = dropdown.options[dropdown.selectedIndex];
      if (selectedOption) {
        // Extract the dates from the option's attributes
        const startDateStr = selectedOption.getAttribute('data-start-date');
        const endDateStr = selectedOption.getAttribute('data-end-date');

        // Convert dates from DD/MM/YYYY to YYYY-MM-DD format
        const startDate = this.convertDateToISO(startDateStr);
        const endDate = this.convertDateToISO(endDateStr);

        // Update the store's date properties
        this.startDate = startDate;
        this.endDate = endDate;

        this.updatePrice();
        // Clear date warning if both dates are set
        if (this.startDate && this.endDate) {
          this.clearValidationError('startEndDate');
        }
      }
    },
    closeForm() {
      closeAllForms();
      document.body.classList.remove('slideform-open'); // This is to prevent the background from scrolling when the form is open
      this.hideInitializingOverlayImmediate();
      // Deactivate backdrop
      document.getElementById('backdrop').style.display = 'none';

      // Clear all validation errors when form is closed
      this.validationErrors = {
        peopleUnits: '',
        startEndDate: '',
        personType: '',
        channexComplexChange: '',
      };

      // Reset custom fields to fresh state (like a page reload)
      this.customFields = {};
      this.customFieldsPerPerson = {};
    },
    initializeFlatpickr(product, flatpickrElement, skipDefaultDates = false) {
      // Determine the mode based on tour duration toggle for tours with range capability
      let mode = this.dateSelectType;
      if (this.showTourDurationToggle) {
        mode = this.tourDuration === 'single' ? 'single' : 'range';
      }

      const isSingleMode = mode === 'single';

      this.flatpickrInstances[product] = flatpickr(flatpickrElement, {
        disableMobile: 'true',
        mode: mode,
        dateFormat: 'd/m/Y',
        showMonths:
          mode === 'range'
            ? window.matchMedia('(min-width: 769px)').matches
              ? 2
              : 1
            : 1, // Show two months on desktop, one on mobile for range selection
        defaultDate: skipDefaultDates
          ? null
          : isSingleMode
          ? [this.initialStartDate]
          : [this.initialStartDate, this.initialEndDate],

        onChange: (selectedDates, dateStr, instance) => {
          this.startDate = selectedDates[0]
            ? formatDateISO(selectedDates[0])
            : '';
          this.endDate = isSingleMode
            ? this.startDate
            : selectedDates[1]
            ? formatDateISO(selectedDates[1])
            : '';

          const startDate = selectedDates[0];

          // Enable nextMonth arrow
          if (instance && instance.nextMonthNav) {
            instance.nextMonthNav.classList.remove('flatpickr-disabled'); // Remove disabled class
            instance.nextMonthNav.setAttribute('aria-disabled', 'false'); // Reset accessibility attribute
            instance.nextMonthNav.onclick = null; // Re-enable navigation
          }

          // Re-enable the year selector
          if (instance && instance.currentYearElement) {
            instance.currentYearElement.removeAttribute('disabled'); // Re-enable the year selector
            instance.currentYearElement.classList.remove('flatpickr-disabled'); // Remove the disabled class
          }

          // Re-enable the month dropdown (if it exists)
          if (instance && instance.monthElements) {
            instance.monthElements.forEach((monthElement) => {
              monthElement.removeAttribute('disabled'); // Re-enable the month selector
              monthElement.classList.remove('flatpickr-disabled'); // Remove the disabled class
            });
          }

          if (selectedDates.length === 1) {
            // Disable all dates before the selected start date and restrict backward selection
            instance.set('disable', [
              function (date) {
                return date < startDate;
              },
            ]);

            // Update the minDate to prevent backward selection for end date
            instance.set('minDate', startDate);

            // Refresh availability with selected start date for accurate end-date constraints
            this.fetchUnavailableDates(
              startDate.getFullYear().toString(),
              (startDate.getMonth() + 1).toString(),
              instance,
              startDate,
            );

            // Ensure restrictions are updated
            this.updateFlatpickrDates(product, startDate);

            // Package end-date constraints are validated in backend get-unavailable-dates.
            // Keep frontend as a renderer of backend unavailable_dates/closed_to_days.
          }

          // Remove original dates warning when user selects new dates
          if (this.startDate && this.endDate) {
            this.removeOriginalDates();
          }

          this.updatePrice();

          if (this.startDate && this.endDate) {
            this.clearValidationError('startEndDate');
          }
        },

        // Reset and update Flatpickr when it's opened
        onOpen: (selectedDates, dateStr, instance) => {
          console.log('Flatpickr Opened');

          // Add loading class immediately when opening
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.add('loading-availability');
          }

          this.showOriginalDates();

          // Clear the selected dates and reset all restrictions
          instance.clear();

          // Reset all date restrictions
          instance.set('disable', []); // Clear all existing restrictions
          instance.set('minDate', null); // Clear any minimum date restriction
          instance.set('maxDate', null); // Clear any maximum date restriction

          // Ensure all dates are disabled initially until data is fetched
          instance.set('disable', [
            function () {
              return true; // Disable all dates initially
            },
          ]);

          // Create date object from initialStartDate and jump to that month
          const initialDate = new Date(
            this.initialStartDate.split('/').reverse().join('-') + 'T00:00:00Z',
          );
          instance.jumpToDate(initialDate);

          // Use the initial date's month and year for fetching availability
          const initialYear = initialDate.getFullYear();
          const initialMonth = initialDate.getMonth() + 1;
          this.fetchUnavailableDates(
            initialYear.toString(),
            initialMonth.toString(),
            instance,
          );

          // Call updateFlatpickrDates to apply any additional logic
          this.updateFlatpickrDates(product, this.initialStartDate);
        },

        onMonthChange: (selectedDates, dateStr, instance) => {
          console.log('Month Changed');

          // Add loading class when month changes
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.add('loading-availability');
          }

          instance.set('disable', [
            function () {
              return true;
            },
          ]);

          // Fetch unavailable dates for the new month
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1;
          const startDate = selectedDates[0] || null;
          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
            startDate,
          );

          // Reapply the date logic after the month changes
          this.updateFlatpickrDates(product, startDate);
        },

        onYearChange: (selectedDates, dateStr, instance) => {
          console.log('Year Changed');

          // Add loading class when year changes
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.add('loading-availability');
          }

          instance.set('disable', [
            function () {
              return true;
            },
          ]);

          // Fetch unavailable dates for the new year and month
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1;
          const startDate = selectedDates[0] || null;
          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
            startDate,
          );

          // Reapply the date logic after the year changes
          this.updateFlatpickrDates(product, startDate);
        },
      });

      // Set the initial jump-to-date based on the initial start date
      const startDateObj = new Date(
        this.initialStartDate.split('/').reverse().join('-') + 'T00:00:00Z',
      );
      if (!isNaN(startDateObj)) {
        console.log('Jumping to Date:', startDateObj);
        this.flatpickrInstances[product].jumpToDate(startDateObj);
      }
    },
    updateFlatpickrDates(product, startDate = null) {
      const instance = this.flatpickrInstances[product];

      if (instance && instance.daysContainer) {
        // Disable unavailable dates plus package-blocked start dates in pre-start mode.
        let disabledDates = [...this.unavailableDates];
        if (!startDate) {
          disabledDates = [
            ...new Set([...disabledDates, ...this.packageClosedToStartDates]),
          ];
        }

        // Set only unavailable dates initially
        instance.set('disable', disabledDates);

        // Convert categorized_dates arrays into Set for efficient lookup
        this.categorizedDates = {
          start: new Set(this.categorizedDates.start),
          single: new Set(this.categorizedDates.single),
          single2: new Set(this.categorizedDates.single2),
          middle: new Set(this.categorizedDates.middle),
          end: new Set(this.categorizedDates.end),
        };

        const unavailableDateSet = new Set(this.unavailableDates);

        const currentMonth = instance.currentMonth; // Track the currently viewed month
        const currentYear = instance.currentYear;

        const dayElements =
          instance.daysContainer.querySelectorAll('.flatpickr-day');

        dayElements.forEach((dayElem) => {
          const ariaLabel = dayElem.getAttribute('aria-label');
          const dateStr = this.convertAriaLabelToDateStr(ariaLabel); // Convert to DD-MM-YYYY format
          const dateObj = new Date(ariaLabel); // Create Date object for comparison

          // Extract the month and year of the current date
          const elemMonth = dateObj.getMonth(); // Month is 0-indexed
          const elemYear = dateObj.getFullYear();

          // Disable dates before the startDate, if startDate is selected, but only in the same month
          if (
            startDate &&
            dateObj < startDate &&
            elemMonth === currentMonth &&
            elemYear === currentYear
          ) {
            dayElem.classList.add('flatpickr-disabled');
            dayElem.removeAttribute('tabindex'); // Prevent focusing on the disabled date
            return; // Skip further checks for this date
          }

          // Apply custom styling based on categorized dates
          if (this.categorizedDates.start.has(dateStr)) {
            dayElem.classList.add('start-date');
          }
          if (this.categorizedDates.single.has(dateStr)) {
            dayElem.classList.add('single-date');
          }
          if (this.categorizedDates.single2.has(dateStr)) {
            dayElem.classList.add('single2-date');
          }
          if (this.categorizedDates.middle.has(dateStr)) {
            dayElem.classList.add('middle-date');
          }
          if (this.categorizedDates.end.has(dateStr)) {
            dayElem.classList.add('end-date');
          }

          // Initial State: When the picker opens
          if (!this.startDate) {
            if (this.packageClosedToStartDates.includes(dateStr)) {
              dayElem.removeAttribute('tabindex');
              dayElem.onclick = (e) => e.preventDefault();
            }

            if (
              this.categorizedDates.single.has(dateStr) ||
              this.categorizedDates.start.has(dateStr)
            ) {
              dayElem.classList.add('has-ct-arrival');
              dayElem.classList.add('flatpickr-disabled-start');
              dayElem.removeAttribute('tabindex');
              dayElem.onclick = (e) => e.preventDefault(); // Prevent selection on click
              // Tooltip for closed-to-arrival
              dayElem.classList.add('has-tooltip');
              dayElem._tippyClosed = tippy(dayElem, {
                content: 'Closed to Arrival',
                theme: 'light-border',
                trigger: 'mouseenter focus',
              });
            }
          } else if (this.startDate) {
            // After Start Date is Selected: Handle closed-to-arrival and closed-to-departure

            // Ensure the categorized dates (start, single, middle, end) still have their classes
            if (this.categorizedDates.start.has(dateStr)) {
              dayElem.classList.add('start-date');
            }
            if (this.categorizedDates.single.has(dateStr)) {
              dayElem.classList.add('single-date');
            }
            if (this.categorizedDates.single2.has(dateStr)) {
              dayElem.classList.add('single2-date');
            }
            if (this.categorizedDates.middle.has(dateStr)) {
              dayElem.classList.add('middle-date');
            }
            if (this.categorizedDates.end.has(dateStr)) {
              dayElem.classList.add('end-date');
            }

            // Disable previous month arrow once startDate has been selected
            if (instance && instance.prevMonthNav) {
              instance.prevMonthNav.classList.add('flatpickr-disabled'); // Add a disabled class
              instance.prevMonthNav.setAttribute('aria-disabled', 'true'); // Set accessibility attribute
              instance.prevMonthNav.onclick = (e) => e.preventDefault(); // Disable navigation
            }

            // Enable the start and single dates for selection
            if (
              (this.categorizedDates.start.has(dateStr) ||
                this.categorizedDates.single.has(dateStr)) &&
              !unavailableDateSet.has(dateStr)
            ) {
              dayElem.classList.remove('flatpickr-disabled');
              dayElem.setAttribute('tabindex', '0');
            }

            // Disable dates before the startDate, but only within the same month and year
            if (
              startDate &&
              dateObj < startDate &&
              elemMonth === currentMonth &&
              elemYear === currentYear
            ) {
              dayElem.classList.add('flatpickr-disabled');
              dayElem.removeAttribute('tabindex'); // Prevent focusing on the disabled date
              return; // Skip further checks for this date
            }

            // Do not infer an upper bound from categorized date classes in end-date mode.
            // Backend `unavailable_dates` is the source of truth for valid end dates.
          }
        });
      } else {
        console.error(`Flatpickr instance for '${product}' not found.`);
      }
    },
    showOriginalDates() {
      // Find the original dates container specific to the current product
      const originalDatesContainer = document.getElementById(
        `original-dates-container-${this.product}`,
      );

      if (!originalDatesContainer) {
        console.error(
          `Original dates container for product ${this.product} not found`,
        );
        return;
      }

      // Check if the "Original Dates" element already exists, if not, create it
      const originalDatesId = `flatpickr-original-dates-${this.product}`;
      let originalDatesElem = document.getElementById(originalDatesId);

      if (!originalDatesElem) {
        originalDatesElem = document.createElement('small');
        originalDatesElem.id = originalDatesId;
        originalDatesElem.style.display = 'block'; // Ensure it is block level for separation
        originalDatesElem.style.marginTop = '8px'; // Add spacing between the label and the form
        originalDatesElem.style.marginLeft = '10px';
        // Handle case where start date is the same as the end date
        if (this.initialStartDate === this.initialEndDate) {
          originalDatesElem.innerHTML = `Original Date: ${this.initialStartDate}`;
        } else {
          originalDatesElem.innerHTML = `Original Dates: ${this.initialStartDate} to ${this.initialEndDate}`;
        }

        // Insert the element inside the corresponding container
        originalDatesContainer.appendChild(originalDatesElem);
      }
    },
    showOriginalDatesWithWarning(warningMessage) {
      // Find the original dates container specific to the current product
      const originalDatesContainer = document.getElementById(
        `original-dates-container-${this.product}`,
      );

      if (!originalDatesContainer) {
        console.error(
          `Original dates container for product ${this.product} not found`,
        );
        return;
      }

      // Check if the "Original Dates" element already exists, if not, create it
      const originalDatesId = `flatpickr-original-dates-${this.product}`;
      let originalDatesElem = document.getElementById(originalDatesId);

      if (!originalDatesElem) {
        originalDatesElem = document.createElement('div');
        originalDatesElem.id = originalDatesId;
        originalDatesElem.style.marginTop = '8px';
        originalDatesElem.style.marginLeft = '10px';
        originalDatesElem.className =
          'alert alert-warning-soft d-flex align-items-start py-2 px-3 mb-2';

        // Format date display
        let dateDisplay;
        if (this.initialStartDate === this.initialEndDate) {
          dateDisplay = `Original Date: ${this.initialStartDate}`;
        } else {
          dateDisplay = `Original Dates: ${this.initialStartDate} to ${this.initialEndDate}`;
        }

        originalDatesElem.innerHTML = `
          <i class="fas fa-exclamation-triangle me-2 text-warning" style="margin-top: 2px;"></i>
          <div class="small">
            <div><strong>${dateDisplay}</strong></div>
            <div class="text-muted mt-1">${warningMessage}</div>
          </div>
        `;

        // Insert the element inside the corresponding container
        originalDatesContainer.appendChild(originalDatesElem);
      }
    },
    removeOriginalDates() {
      // Find the original dates element specific to the current product
      const originalDatesId = `flatpickr-original-dates-${this.product}`;
      const originalDatesElem = document.getElementById(originalDatesId);

      if (originalDatesElem) {
        // Remove the element from the DOM
        originalDatesElem.remove();
        console.log(`Original Dates for ${this.product} removed`);
      } else {
        console.log(`Original Dates element for ${this.product} not found`);
      }
    },
    fetchUnavailableDates(year, month, instance, startDate = null) {
      const csrfToken = getCSRFToken();

      // Add loading class to show pulsating animation
      if (instance && instance.calendarContainer) {
        instance.calendarContainer.classList.add('loading-availability');
      }

      // Check if we're using 2 months (range mode)
      // If tour duration toggle exists, use the user's choice; otherwise use RateSet's dateSelectType
      const isTwoMonths = this.showTourDurationToggle
        ? this.tourDuration !== 'single'
        : this.dateSelectType === 'range';

      let requestData = {
        year: year,
        month: month,
        soldAs: this.soldAs,
        unitId: this.unitId,
        bookingItemId: this.bookingItemId,
        selectedPeople: this.selectedPeople,
        two_month_mode: isTwoMonths,
        context: 'admin', // Identify this as Admin (skip min constraints)
      };
      if (startDate) {
        requestData.start_date_selected = formatDateISO(startDate);
      }
      // Disable date selection until availability has been fetched and updated
      this.disableFlatpickr(instance, true);

      postJSONWithCSRFRetry('/bookings_app/get-unavailable-dates/', requestData)
        .then(async (response) => {
          const data = await safeJsonParse(response);
          return data;
        })
        .then((data) => {
          this.categorizedDates = data.categorized_dates;
          this.unavailableDates =
            data.unavailable_dates || data.categorized_dates.middle;
          this.minMaxDays = data.min_max_days || {}; // Store min/max constraints
          this.packageClosedToStartDates =
            data.package_closed_to_start_dates || [];
          this.packages = data.packages || []; // Store package info

          this.updateFlatpickrDates(this.product, startDate);
          // Re-enable date selection now that availability has been updated
          this.disableFlatpickr(instance, false);

          // Remove loading class after data is loaded
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.remove('loading-availability');
          }
        })
        .catch((error) => {
          console.error('Error fetching unavailable dates:', error);

          // Remove loading class even on error
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.remove('loading-availability');
          }
        });
    },
    updatePrice() {
      if (this.priceLocked) {
        this.price = parseFloat(this.originalPrice) || 0;
        this.discountAmount = parseFloat(this.lockedDiscountAmount) || 0;
        this.appliedDiscounts = (this.lockedAppliedDiscounts || []).map(
          (d) => ({
            ...d,
          }),
        );
        this.priceUpdated = false;
        this.priceLoading = false;
        this.preserveModeWarning = null;
        return;
      }

      const csrfToken = getCSRFToken();
      console.log('updatePrice called! completeReprice:', this.completeReprice);

      // Get currently selected RateSet from the dropdown directly
      // This ensures we get the current value, not the potentially stale soldAs value
      const rateSetSelector =
        this.product === 'Accommodation'
          ? 'select[name="accommodation_rate_set"]'
          : 'select[name="tour_rate_set"]';
      const rateSetDropdown = document.querySelector(rateSetSelector);
      const currentRateSetId = rateSetDropdown
        ? rateSetDropdown.value
        : this.soldAs;

      // Update soldAs with the current value BEFORE checking warnings
      this.soldAs = currentRateSetId;

      // Update preserve mode warning based on current changes (after soldAs is updated)
      this.updatePreserveModeWarning();

      // Check if dates have changed from the original booking
      const datesChanged =
        this.startDate !== this.initialStartDate ||
        this.endDate !== this.initialEndDate;

      // In PRESERVE mode: Only call API if dates changed (to calculate new dates' prices)
      // If only people/rateset changed, the existing prices are preserved - no need to recalculate
      if (!this.completeReprice && !datesChanged) {
        console.log('PRESERVE MODE: Dates unchanged, keeping original price');
        // Revert to original price (in case user was in reprice mode before)
        this.price = this.originalPrice;
        // Clear any discounts from reprice mode
        this.discountAmount = 0;
        this.appliedDiscounts = [];
        // Price stays as original, warning is already shown by updatePreserveModeWarning()
        this.priceLoading = false;
        return;
      }

      // We need to calculate price - show loading state
      this.priceLoading = true;
      this.priceUpdated = false;

      if (this.soldAs) {
        const rateSetId = this.soldAs;
        console.log('selectedPeople:', this.selectedPeople);
        // Fetch the selected RateSet data from the server
        fetch(`/rates/rate-set/${rateSetId}/`)
          .then(async (response) => {
            const data = await safeJsonParse(response);
            return data;
          })
          .then((selectedRateSet) => {
            if (!selectedRateSet) {
              console.error('Selected RateSet not found');
              return;
            }

            let requestData;
            if (selectedRateSet.is_fixed === true) {
              console.log('FIXED INTERVALS');
              // Prepare the data to be sent
              requestData = {
                product: this.product,
                startDate: this.startDate,
                endDate: this.endDate,
                soldAs: this.soldAs,
                units: 1,
                selectedPeople: this.selectedPeople,
                completeReprice: this.completeReprice,
              };
            } else {
              console.log('FLEXI DATES - Preparing request data:', {
                startDate: this.startDate,
                endDate: this.endDate,
                isFixed: false,
                completeReprice: this.completeReprice,
              });

              requestData = {
                product: this.product,
                startDate: this.startDate,
                endDate: this.endDate,
                soldAs: this.soldAs,
                units: 1,
                selectedPeople: this.selectedPeople,
                bookingItemId: this.bookingItemId,
                completeReprice: this.completeReprice,
              };
            }

            fetch('/bookings_app/calculate-price/', {
              method: 'POST',
              body: JSON.stringify(requestData),
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
              },
            })
              .then(async (response) => {
                const data = await safeJsonParse(response);
                return data;
              })
              .then((data) => {
                console.log('=== DISCOUNT DEBUG (Frontend) ===');
                console.log('Response data:', data);
                console.log('Discount amount:', data.discount_amount);
                console.log('Applied discounts:', data.applied_discounts);
                console.log('Complete reprice mode:', this.completeReprice);
                console.log('Original price:', this.originalPrice);

                // Check if dates have changed from the original booking
                const datesChanged =
                  this.startDate !== this.initialStartDate ||
                  this.endDate !== this.initialEndDate;

                console.log('Dates changed:', datesChanged);
                console.log(
                  'Current dates:',
                  this.startDate,
                  'to',
                  this.endDate,
                );
                console.log(
                  'Initial dates:',
                  this.initialStartDate,
                  'to',
                  this.initialEndDate,
                );

                // Always use backend calculated price - it handles both:
                // - Preserve mode (completeReprice=false): preserves existing dates, adds new at current rates
                // - Complete reprice mode (completeReprice=true): full recalculation with discounts
                this.price = data.price;

                // Update discount information if present
                if (
                  data.applied_discounts &&
                  data.applied_discounts.length > 0
                ) {
                  this.discountAmount = parseFloat(data.discount_amount);
                  this.appliedDiscounts = this.normalizeAppliedDiscounts(
                    data.applied_discounts,
                  );
                  console.log(
                    'Updated appliedDiscounts:',
                    this.appliedDiscounts,
                  );
                } else {
                  this.discountAmount = 0;
                  this.appliedDiscounts = [];
                  console.log('Reset appliedDiscounts to empty array');
                }

                // Price loaded successfully - hide loading, show result
                this.priceLoading = false;
                this.triggerPriceUpdateEffect();

                // Check for complex Channex modifications
                this.checkChannexComplexChange();
              })
              .catch((error) => {
                console.error('Error fetching price:', error);
                this.priceLoading = false;
              });
          })
          .catch((error) => {
            console.error('Error fetching rate set:', error);
            this.priceLoading = false;
          });
      } else {
        console.log('Start date or soldAs is not set');
        this.priceLoading = false;
      }
    },

    handlePriceLockToggle() {
      if (this.priceLocked) {
        this.priceLocked = false;
        this.updatePreserveModeWarning();
        this.updatePrice();
        return;
      }

      this.priceLocked = true;
      this.completeReprice = false;
      // Revert display to the snapshot from when the form opened (not a preview
      // price from date/rate changes made before lock was enabled).
      this.price = parseFloat(this.originalPrice) || 0;
      this.discountAmount = parseFloat(this.lockedDiscountAmount) || 0;
      this.appliedDiscounts = (this.lockedAppliedDiscounts || []).map((d) => ({
        ...d,
      }));
      this.priceUpdated = false;
      this.preserveModeWarning = null;
      this.priceLoading = false;
    },

    // Handle reprice toggle change - recalculate price when toggle state changes
    handleRepriceToggle() {
      if (this.completeReprice) {
        this.priceLocked = false;
      }
      console.log('Reprice toggle changed:', this.completeReprice);
      // Update warning message based on new toggle state
      this.updatePreserveModeWarning();

      // Check if dates have changed
      const datesChanged =
        this.startDate !== this.initialStartDate ||
        this.endDate !== this.initialEndDate;

      if (this.completeReprice) {
        // Switched TO reprice mode - always recalculate
        this.updatePrice();
      } else if (!datesChanged) {
        // Switched TO preserve mode AND dates haven't changed
        // Just revert to original price immediately - no API call needed
        console.log(
          'Switched to preserve mode, reverting to original price:',
          this.originalPrice,
        );
        this.price = this.originalPrice;
        this.discountAmount = 0;
        this.appliedDiscounts = [];
        this.priceLoading = false;
      } else {
        // Switched TO preserve mode BUT dates have changed
        // Need to recalculate: preserved existing dates + new dates at current rates
        this.updatePrice();
      }
    },

    // Compute preserve mode warning when user makes pricing-affecting changes
    // Only shows warning when: rate set changes OR people count changes (when people affect price)
    updatePreserveModeWarning() {
      if (this.priceLocked) {
        this.preserveModeWarning = null;
        return;
      }

      // Only show warning if in preserve mode (completeReprice = false)
      if (this.completeReprice) {
        this.preserveModeWarning = null;
        return;
      }

      // Check for rate set change (convert to string for safe comparison, handle null/undefined)
      const currentRateSetId = this.soldAs ? String(this.soldAs) : '';
      const originalRateSetId = this.initialRateSetId
        ? String(this.initialRateSetId)
        : '';
      // Only consider it a change if BOTH values exist and are different
      const rateSetChanged =
        currentRateSetId !== '' &&
        originalRateSetId !== '' &&
        currentRateSetId !== originalRateSetId;

      // Check for people change - only if people_influence_price is true on CURRENT rate set
      // Note: For person types (adults/children), selectedPeople is calculated as adults + children
      const currentPeople = parseInt(this.selectedPeople) || 0;
      const originalPeople = parseInt(this.initialPeople) || 0;
      // Only warn about people change if people actually influence price for this rate set
      const peopleChanged =
        currentPeople !== originalPeople && this.peopleInfluencePrice === true;

      console.log('Preserve mode warning check:', {
        rateSetChanged,
        currentRateSetId,
        originalRateSetId,
        peopleChanged,
        currentPeople,
        originalPeople,
        peopleInfluencePrice: this.peopleInfluencePrice,
        completeReprice: this.completeReprice,
      });

      // Show generic warning if any pricing-affecting change was made
      if (rateSetChanged || peopleChanged) {
        this.preserveModeWarning =
          'Prices were kept for existing dates.\nChoose Complete reprice to update pricing.';
      } else {
        this.preserveModeWarning = null;
      }
    },

    checkChannexComplexChange() {
      // Only check if this is a Channex item
      if (!this.externalItemId) {
        this.validationErrors.channexComplexChange = '';
        return;
      }

      const normalizeDateValue = (value) => {
        if (!value) return '';
        const str = String(value).trim();

        // Already ISO-like: YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

        // Display format: DD/MM/YYYY -> YYYY-MM-DD
        const slashMatch = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (slashMatch) {
          const [, dd, mm, yyyy] = slashMatch;
          return `${yyyy}-${mm}-${dd}`;
        }

        // Fallback: try Date parsing and normalize to UTC date string
        const parsed = parseDate(str);
        if (parsed && !Number.isNaN(parsed.getTime())) {
          return formatDateISO(parsed);
        }
        return str;
      };

      const rateSetChanged =
        this.initialSoldAs &&
        this.soldAs &&
        String(this.initialSoldAs) !== String(this.soldAs);
      const unitChanged =
        this.initialUnitId &&
        this.unitId &&
        String(this.initialUnitId) !== String(this.unitId);
      const initialStartISO = normalizeDateValue(this.initialStartDate);
      const initialEndISO = normalizeDateValue(this.initialEndDate);
      const currentStartISO = normalizeDateValue(this.startDate);
      const currentEndISO = normalizeDateValue(this.endDate);
      const datesChanged =
        initialStartISO !== currentStartISO || initialEndISO !== currentEndISO;
      if (datesChanged || this.completeReprice) {
        this.validationErrors.channexComplexChange =
          'This OTA booking item is locked in Hightide. Date and reprice changes must be made on the OTA platform.';
        return;
      }

      const currentPeople = parseInt(this.selectedPeople || 0);
      const initialPeople = parseInt(this.initialPeople || 0);
      const currentAdults = parseInt(this.selectedAdults || 0);
      const initialAdults = parseInt(this.initialAdults || 0);
      const currentChildren = parseInt(this.selectedChildren || 0);
      const initialChildren = parseInt(this.initialChildren || 0);
      const isPersonUnitType =
        this.unitType === 'Person' ||
        this.unitType === 'Adult' ||
        this.unitType === 'Child';
      // For person-type-enabled OTA forms, switching adult/child distribution can be
      // valid as long as total people remains unchanged.
      let occupancyChanged = false;
      if (isPersonUnitType) {
        occupancyChanged = currentPeople !== initialPeople;
      } else if (this.usePersonTypes === true) {
        // For Adult/Child split forms, allow occupancy edits in Hightide.
        occupancyChanged = false;
      } else {
        occupancyChanged =
          currentPeople !== initialPeople ||
          currentAdults !== initialAdults ||
          currentChildren !== initialChildren;
      }

      if (occupancyChanged) {
        this.validationErrors.channexComplexChange =
          'OTA occupancy changes must be made on the OTA platform. In Hightide you can edit person type, adult/child fields, custom fields, flags, or move room/listing.';
        return;
      }

      this.validationErrors.channexComplexChange = '';
    },
    shouldShowOtaMoveImpactConfirmation() {
      if (!this.externalItemId) return false;

      const rateSetChanged =
        this.initialSoldAs &&
        this.soldAs &&
        String(this.initialSoldAs) !== String(this.soldAs);
      const unitChanged =
        this.initialUnitId &&
        this.unitId &&
        String(this.initialUnitId) !== String(this.unitId);

      return Boolean(rateSetChanged || unitChanged);
    },
    resetFlatpickr(product, flatpickrElement) {
      if (this.flatpickrInstances[product]) {
        console.log(
          'Destroying existing Flatpickr instance for product:',
          product,
        );
        this.flatpickrInstances[product].destroy();
        flatpickrElement.value = ''; // Clear the input field
      }

      this.initializeFlatpickr(product, flatpickrElement, this.dateSelectMode);
    },
    updateInitialDateForIdentifier(product, isoDate) {
      if (this.flatpickrInstances[product]) {
        const newDate = parseDate(isoDate);
        if (newDate) {
          this.flatpickrInstances[product].jumpToDate(newDate, true);
        }
      }
    },
    updateInitialDates(isFixed, isoStartDate, isoEndDate) {
      if (isFixed === true) {
        this.showFixedDateRange = true;
        this.initialStartDate = this.convertDateFormat(isoStartDate);
        this.initialEndDate = this.convertDateFormat(isoEndDate);
        this.populateFixedDateRangeDropdown(
          this.product,
          this.soldAs,
          this.initialStartDate,
          this.initialEndDate,
        );
      } else {
        this.showFixedDateRange = false;
        this.initialStartDate = this.convertDateFormat(isoStartDate);
        this.initialEndDate = this.convertDateFormat(isoEndDate);
        if (this.flatpickrInstance) {
          this.flatpickrInstance.setDate(
            [this.initialStartDate, this.initialEndDate],
            true,
          );
        }
      }
    },
    convertDateFormat(isoDate) {
      if (!isoDate) return '';
      const [year, month, day] = isoDate.split('-');
      return `${day}/${month}/${year}`;
    },
    convertDateToISO(dateStr) {
      const parts = dateStr.split('/');
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    },
    triggerPriceUpdateEffect() {
      this.priceUpdated = true;

      // Trigger CSS animation for price value
      const priceInput = document.querySelector('#price');
      if (priceInput) {
        priceInput.classList.add('price-animating');
        setTimeout(() => {
          priceInput.classList.remove('price-animating');
        }, 1800); // Match animation duration
      }

      setTimeout(() => {
        this.priceUpdated = false;
      }, 1800); // Duration of the background color effect
    },
    disableFlatpickr(flatpickrInstance, disable) {
      // Disable or enable the input field
      flatpickrInstance.input.disabled = disable;

      // Disable or enable the prev/next navigation buttons, if they exist
      if (flatpickrInstance.prevMonthNav && flatpickrInstance.nextMonthNav) {
        flatpickrInstance.prevMonthNav.disabled = disable;
        flatpickrInstance.nextMonthNav.disabled = disable;
      }

      // Optionally, disable the year and month dropdowns if present
      const pluginElements =
        flatpickrInstance.calendarContainer.querySelectorAll(
          '.flatpickr-monthDropdown-months, .numInput',
        );
      pluginElements.forEach((element) => {
        element.disabled = disable;
      });
    },
    resetDatesAndPrice(clearDates = true) {
      console.log('Start dates and price reset...');

      if (clearDates) {
        // Clear dates
        this.startDate = '';
        this.endDate = '';

        // Only reset price to 0 when dates are actually being cleared
        // (user will need to select new dates, then updatePrice will calculate)
        this.price = 0;
        // Clear discount information
        this.discountAmount = 0;
        this.appliedDiscounts = [];
      }
      // Note: If clearDates is false, keep the current price
      // The caller is responsible for calling updatePrice() if a recalculation is needed

      // Reset fixed date range selection to the first option if needed
      this.fixedDateRange = '';
      const dropdown = document.querySelector('.fixedDateRangeDropdown');
      dropdown.innerHTML = ''; // Clear existing options

      if (clearDates) {
        this.showOriginalDates();
      }

      console.log('Dates and price reset sucessfully');
    },
    async checkDateAvailability(soldAsId, unitId) {
      console.log('Checking date availability for original dates...');

      const selectedStartDate = this.startDate || this.initialStartDate;
      const selectedEndDate = this.endDate || this.initialEndDate;

      // If no dates set, return false
      if (!selectedStartDate || !selectedEndDate) {
        return false;
      }

      const csrfToken = getCSRFToken();

      // Convert YYYY-MM-DD or DD/MM/YYYY to YYYY-MM-DD for API
      const normalizeDateInput = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.includes('-')) return dateStr;
        const [day, month, year] = dateStr.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      };

      const startDateISO = normalizeDateInput(selectedStartDate);
      const endDateISO = normalizeDateInput(selectedEndDate);
      const startDateObj = parseDate(startDateISO);
      const endDateObj = parseDate(endDateISO);
      if (!startDateObj || !endDateObj) {
        return false;
      }

      try {
        const response = await fetch('/bookings_app/get-unavailable-dates/', {
          method: 'POST',
          body: JSON.stringify({
            year: startDateObj.getFullYear().toString(),
            month: (startDateObj.getMonth() + 1).toString(),
            soldAs: soldAsId,
            unitId: unitId,
            bookingItemId: this.bookingItemId,
            amountUnits: this.units,
            selectedPeople: this.selectedPeople,
            two_month_mode: this.dateSelectType === 'range',
            context: 'admin',
          }),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
        });

        const data = await response.json();
        const unavailableDates = new Set(
          data.unavailable_dates || data.categorized_dates?.middle || [],
        );
        const packageClosedToStartDates = new Set(
          data.package_closed_to_start_dates || [],
        );

        // Helper function to convert YYYY-MM-DD to DD-MM-YYYY
        const formatDateDDMMYYYY = (isoDate) => {
          const [year, month, day] = isoDate.split('-');
          return `${day}-${month}-${year}`;
        };

        // Check if any date in the range is unavailable
        const start = new Date(startDateObj);
        const end = new Date(endDateObj);
        const current = new Date(start);
        let isFirstDate = true;

        while (current <= end) {
          const dateStrISO = formatDateISO(current);
          const dateStrFormatted = formatDateDDMMYYYY(dateStrISO);

          if (unavailableDates.has(dateStrFormatted)) {
            console.log(
              `Date ${dateStrISO} (${dateStrFormatted}) is unavailable`,
            );
            return false;
          }
          if (isFirstDate && packageClosedToStartDates.has(dateStrFormatted)) {
            console.log(
              `Start date ${dateStrISO} (${dateStrFormatted}) blocked by required package component availability`,
            );
            return false;
          }
          isFirstDate = false;
          current.setDate(current.getDate() + 1);
        }

        const startAwareResponse = await fetch(
          '/bookings_app/get-unavailable-dates/',
          {
            method: 'POST',
            body: JSON.stringify({
              year: startDateObj.getFullYear().toString(),
              month: (startDateObj.getMonth() + 1).toString(),
              soldAs: soldAsId,
              unitId: unitId,
              bookingItemId: this.bookingItemId,
              amountUnits: this.units,
              selectedPeople: this.selectedPeople,
              two_month_mode: this.dateSelectType === 'range',
              context: 'admin',
              start_date_selected: startDateISO,
            }),
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrfToken,
            },
          },
        );

        const startAwareData = await startAwareResponse.json();
        const startAwareUnavailableDates = new Set(
          startAwareData.unavailable_dates ||
            startAwareData.categorized_dates?.middle ||
            [],
        );
        const selectedEndFormatted = formatDateDDMMYYYY(endDateISO);
        if (startAwareUnavailableDates.has(selectedEndFormatted)) {
          console.log(
            `Selected end date ${endDateISO} (${selectedEndFormatted}) is unavailable for start ${startDateISO}`,
          );
          return false;
        }

        console.log('Original dates are available');
        return true;
      } catch (error) {
        console.error('Error checking date availability:', error);
        return false;
      }
    },
    resetFlatpickrDates(soldAsId) {
      // Reset Flatpickr dates here
      Object.values(this.flatpickrInstances).forEach((flatpickrInstance) => {
        flatpickrInstance.clear(); // Clears the selected dates
        // You can also set a default date here if needed
        // flatpickrInstance.setDate("2023-01-01", true); // Example to set a specific date
      });

      console.log('Flatpickr dates reset successfully');
    },
    convertAriaLabelToDateStr(ariaLabel) {
      const date = new Date(ariaLabel);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0'); // getMonth() is zero-indexed
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
    formatCurrency(amount, currency) {
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parseFloat(amount));
      return formatted;
    },
    archiveBookingItem() {
      const csrfToken = getCSRFToken();
      fetch(
        `/bookings_app/calendar-delete-booking-item/${this.bookingItemId}/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
        },
      )
        .then(async (response) => {
          if (!response.ok) throw new Error('Failed to delete booking item');
          const data = await safeJsonParse(response);
          return data;
        })
        .then((data) => {
          console.log('Booking item deleted successfully:', data);
          this.removeDeletedBookingItemFromCalendar(data);
        })
        .catch((error) => {
          console.error('Error archiving booking item:', error);
        });
    },
    removeDeletedBookingItemFromCalendar(data) {
      // Close the form
      this.closeForm();
      const calendarStore = Alpine.store('calendarStore');
      const cartStore = Alpine.store('cartStore');
      const toastStore = Alpine.store('toastStore');
      calendarStore.isLoading = true;

      // Delay the update of the calendarStore to allow the fade-out effect
      setTimeout(() => {
        // Remove the deleted booking item from the calendar store
        const updatedBookingItems = calendarStore.bookingItems.filter(
          (item) => item.item.id !== this.bookingItemId,
        );

        // Remove the blocked dates associated with the deleted booking item
        const updatedCalendarDatesBlocked = {};
        for (const unitId in calendarStore.calendarDatesBlocked) {
          const blockedUnit = calendarStore.calendarDatesBlocked[unitId];
          updatedCalendarDatesBlocked[unitId] = { dates: {} };
          for (const date in blockedUnit.dates) {
            updatedCalendarDatesBlocked[unitId].dates[date] = blockedUnit.dates[
              date
            ].filter((bookingId) => bookingId !== this.bookingItemId);
            // If no bookingIds remain for that date, delete the date entry
            if (updatedCalendarDatesBlocked[unitId].dates[date].length === 0) {
              delete updatedCalendarDatesBlocked[unitId].dates[date];
            }
          }
          // If no dates remain for that unit, delete the unit entry
          if (
            Object.keys(updatedCalendarDatesBlocked[unitId].dates).length === 0
          ) {
            delete updatedCalendarDatesBlocked[unitId];
          }
        }

        // Remove the booked dates associated with the deleted booking item
        const updatedCalendarDatesBooked = {};
        for (const unitId in calendarStore.calendarDatesBooked) {
          const bookedUnit = calendarStore.calendarDatesBooked[unitId];
          updatedCalendarDatesBooked[unitId] = {};
          for (const date in bookedUnit) {
            // If the date was booked by the deleted booking item, remove it
            if (bookedUnit[date] === true) {
              // Check if this date is still booked by other items
              const stillBooked = updatedBookingItems.some((item) => {
                const itemStartDate = parseDate(item.item.start_date);
                const itemEndDate = parseDate(item.item.end_date);
                const checkDate = parseDate(date);
                if (!itemStartDate || !itemEndDate || !checkDate) return false;
                return item.item.accommodation_unit === unitId ||
                  item.item.tour_unit === unitId
                  ? checkDate >= itemStartDate && checkDate < itemEndDate
                  : false;
              });

              if (!stillBooked) {
                // This date is no longer booked, so don't add it to the updated object
                continue;
              }
            }
            updatedCalendarDatesBooked[unitId][date] = bookedUnit[date];
          }
          // If no dates remain for that unit, delete the unit entry
          if (Object.keys(updatedCalendarDatesBooked[unitId]).length === 0) {
            delete updatedCalendarDatesBooked[unitId];
          }
        }

        // If booking item being deleted belongs to the booking in editmode subtract 1 item
        // Check if editModeBooking is not null before trying to access its id
        if (
          cartStore.editModeBooking !== null &&
          cartStore.editModeBooking !== undefined
        ) {
          if (this.bookingId === cartStore.editModeBooking.id) {
            console.log('this.bookingId:', this.bookingId);
            console.log(
              'cartStore.editModeBooking:',
              cartStore.editModeBooking.id,
            );
            cartStore.bookingItemsCount -= 1;
          }
        }

        calendarStore.bookingItems = updatedBookingItems;
        calendarStore.calendarDatesBlocked = updatedCalendarDatesBlocked;
        calendarStore.calendarDatesBooked = updatedCalendarDatesBooked;

        calendarStore.isLoading = false;
        if (toastStore && toastStore.addToast) {
          toastStore.addToast(data.message, 'success');
        }
      }, 1000); // Delay in milliseconds
    },
    validateForm(event, action) {
      const form = event.target.closest('form');
      const toastStore = Alpine.store('toastStore');

      // Ensure the start and end dates are defined
      if (!this.startDate || !this.endDate) {
        event.preventDefault();
        this.setValidationError(
          'startEndDate',
          'Please select a valid start and end date.',
        );
        return;
      }

      // OTA guardrail: blocks date/reprice/occupancy changes, allows person type/metadata edits.
      if (this.externalItemId) {
        this.checkChannexComplexChange();
        if (this.validationErrors.channexComplexChange) {
          event.preventDefault();
          if (toastStore && toastStore.addToast) {
            toastStore.addToast(
              this.validationErrors.channexComplexChange,
              'warning',
            );
          }
          return;
        }
      }

      // Validate person type requirements when rate set uses person types
      // Check for Person, Adult, or Child unit types
      const isPersonUnitType =
        this.unitType === 'Person' ||
        this.unitType === 'Adult' ||
        this.unitType === 'Child';
      if (this.usePersonTypes && isPersonUnitType) {
        if (!this.personType || this.personType === '') {
          event.preventDefault();
          this.setValidationError(
            'personType',
            'Please select a person type for this booking.',
          );
          return;
        }
      }

      // Validate adults/children when rate set uses person types but unit type is not Person
      if (this.usePersonTypes && this.unitType !== 'Person') {
        const totalPeople =
          parseInt(this.selectedAdults || 0) +
          parseInt(this.selectedChildren || 0);
        if (totalPeople === 0) {
          event.preventDefault();
          this.setValidationError(
            'personType',
            'Please select at least one adult or child for this booking.',
          );
          return;
        }
      }

      // Clear person type validation error if validation passes
      this.clearValidationError('personType');

      if (form.checkValidity()) {
        if (this.shouldShowOtaMoveImpactConfirmation()) {
          const proceed = window.confirm(
            'You are changing the room/listing for an OTA booking item.\n\n' +
              'Please make sure this has been agreed with the guest.\n\n' +
              'Important: future updates from the OTA platform may not be auto-applied and could require manual action in Hightide.',
          );

          if (!proceed) {
            event.preventDefault();
            if (toastStore && toastStore.addToast) {
              toastStore.addToast('Room/listing change cancelled.', 'info');
            }
            return;
          }
        }

        // No longer showing modal - the reprice toggle on the form determines the behavior
        // If completeReprice is false (default), we preserve existing prices
        // If completeReprice is true, we do a full reprice with discounts
        this.submitForm(event, action);
      } else {
        form.reportValidity();
      }
    },
    submitForm(event) {
      console.log('FORM SUBMITTED!!');
      this.closeForm();
      const calendarStore = Alpine.store('calendarStore');
      //const cartStore = Alpine.store('cartStore');
      const toastStore = Alpine.store('toastStore');
      calendarStore.isLoading = true;
      // Set flag BEFORE fetch to prevent race condition with WebSocket message
      calendarStore.skipNextWebSocketUpdate = true;
      const formElement = event.target.form || event.currentTarget.form;
      const formData = new FormData(formElement);

      // Add reprice flag based on toggle state
      // completeReprice=true means full reprice with current rates and discounts
      // completeReprice=false (default) means use backend's price preservation logic:
      //   - Existing dates keep their rates
      //   - New dates use current rates (no discount recalculation)
      if (this.completeReprice) {
        formData.append('complete_reprice', 'true');
      }
      if (this.priceLocked) {
        formData.append('lock_price', 'true');
      }
      // When completeReprice=false, don't send any special flag - let backend use
      // its normal create_booking_dates_with_price_preservation() logic

      // Add custom fields to form data by directly reading from form inputs
      if (formElement) {
        const customFieldInputs = formElement.querySelectorAll(
          '[name^="custom_field_"]',
        );
        customFieldInputs.forEach((input) => {
          if (input.type === 'checkbox') {
            if (input.checked) {
              formData.set(input.name, input.value || 'on');
            }
          } else if (input.type === 'radio') {
            if (input.checked) {
              formData.set(input.name, input.value);
            }
          } else {
            formData.set(input.name, input.value);
          }
        });
      }

      fetch(`/bookings_app/${this.bookingItemId}/update/`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRFToken': getCSRFToken(),
        },
      })
        .then((response) =>
          response
            .json()
            .then((data) => ({ status: response.status, body: data })),
        )
        .then(({ status, body }) => {
          if (status >= 400) throw body;
          console.log('Booking updated successfully:', body);
          // Remove Original dates from form
          this.removeOriginalDates();

          // Replace all booking items belonging to new booking and add new ones
          const newItemsMap = new Map(
            body.bookingItems.map((item) => [item.item.id, item]),
          );
          // Filter out any old items that are going to be updated
          let updatedBookingItems = calendarStore.bookingItems.filter(
            (item) => !newItemsMap.has(item.item.id),
          );
          // Then, add all new items (this adds both completely new items and updates existing ones)
          updatedBookingItems = updatedBookingItems.concat(body.bookingItems);
          // Finally, assign the updated list back to the store
          // Check if blocked dates have changed before updating
          const blockedDatesChanged = !deepEqual(
            calendarStore.calendarDatesBlocked,
            body.calendar_dates_blocked,
          );

          if (blockedDatesChanged) {
            if (toastStore && toastStore.addToast) {
              toastStore.addToast(
                'Unit blocks have been updated due to linked availability',
                'info',
              );
            }
          }

          // Update all calendar state simultaneously to prevent flickering
          // This ensures bookingItems and calendarDatesBlocked are updated together
          calendarStore.bookingItems = updatedBookingItems;
          calendarStore.calendarDatesBooked = body.calendar_dates_booked;
          calendarStore.calendarDatesBlocked = body.calendar_dates_blocked;
          // Clear filter cache to ensure updated items are rendered
          calendarStore._bookingItemsCache = null;
          calendarStore._blockItemsCache = null;
          calendarStore.isLoading = false;
          //cartStore.editModeBooking = body.edit_mode_booking;
          //cartStore.bookingItemsCount = body.booking_items_count;
          //toastStore.addToast(body.message, 'success');

          console.log(
            'calendarStore.calendarDatesBlocked:',
            calendarStore.calendarDatesBlocked,
          );

          const fields = document.getElementsByClassName('slideForm-field');
          Array.from(fields).forEach((field) => {
            field.value = '';
          });
        })
        .catch((error) => {
          console.error('Error updating booking:', error);
          calendarStore.isLoading = false;
          let errorMessage = 'An unexpected error occurred';

          if (typeof error === 'string') {
            errorMessage = error;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (Array.isArray(error?.error) && error.error.length > 0) {
            errorMessage = error.error.join(' ');
          } else if (typeof error?.error === 'string') {
            errorMessage = error.error;
          } else if (
            typeof error?.errors === 'object' &&
            error.errors !== null
          ) {
            const firstError = Object.values(error.errors).find((value) =>
              Array.isArray(value) ? value.length > 0 : !!value,
            );
            if (Array.isArray(firstError)) {
              errorMessage = firstError.join(' ');
            } else if (firstError) {
              errorMessage = String(firstError);
            }
          }

          if (toastStore && toastStore.addToast) {
            toastStore.addToast(errorMessage, 'danger');
          }
        });
    },
    handlePriceChangeAbort() {
      console.log('User chose to abort price change');
      this.showPriceChangeModal = false;
      this.priceChangeAction = null;
      this.pendingSubmitEvent = null;
    },
    handlePriceChangeUpdate() {
      console.log('User chose to update price');
      this.showPriceChangeModal = false;
      this.priceChangeAction = 'update';
      // Continue with submission using the new price
      if (this.pendingSubmitEvent) {
        this.submitForm(this.pendingSubmitEvent);
      }
      // Reset for next time
      this.priceChangeAction = null;
      this.pendingSubmitEvent = null;
    },
    handlePriceChangePreserve() {
      console.log('User chose to preserve original price');
      this.showPriceChangeModal = false;
      this.priceChangeAction = 'preserve';
      // Continue with submission but preserve original price
      if (this.pendingSubmitEvent) {
        this.submitForm(this.pendingSubmitEvent);
      }
      // Reset for next time
      this.priceChangeAction = null;
      this.pendingSubmitEvent = null;
    },
    resetStore() {
      console.log('Resetting editBookingItemForm store to initial state');

      // First set all person-related fields to null/false
      this.personType = '';
      this.usePersonTypes = false;
      this.unitType = '';
      this.selectedAdults = 0;
      this.selectedChildren = 0;
      this.peopleAsOption = false;

      // Reset custom fields
      this.selectedRateSet = null;
      this.customFields = {};
      // Simple reset - getPersonFieldsObj() will create objects on demand
      this.customFieldsPerPerson = {};

      // Reset flag
      this.flagColor = '';

      // Then reset everything else
      this.bookingItemId = '';
      this.bookingId = '';
      this.unitId = '';
      this.peoplePerUnit = 0;
      this.amountPeopleOptions = [];
      this.selectedPeople = null;
      this.mainGuest = '';
      this.startDate = '';
      this.endDate = '';
      this.product = 'Accommodation';
      this.soldAs = null;
      this.dateSelectType = 'range';
      this.fixedDateRange = '';
      this.showFixedDateRange = false;
      this.unavailableDates = [];
      this.initialStartDate = '';
      this.initialEndDate = '';
      this.price = 0;
      this.originalPrice = 0;
      this.priceUpdated = false;
      this.priceLoading = false;
      this.hideInitializingOverlayImmediate();
      // Reset discount information
      this.discountAmount = 0;
      this.appliedDiscounts = [];
      // Reset complete reprice toggle
      this.completeReprice = false;
      this.repriceAmount = null;
      this.preservedAmount = null;
      this.priceLocked = false;
      this.lockedFinalPrice = 0;
      this.lockedDiscountAmount = 0;
      this.lockedAppliedDiscounts = [];
      // Reset preserve mode tracking
      this.initialRateSetId = null;
      this.initialPeople = 1;
      this.peopleInfluencePrice = false;
      this.preserveModeWarning = null;
      // Reset price change modal (legacy)
      this.showPriceChangeModal = false;
      this.priceChangeAction = null;
      this.pendingSubmitEvent = null;
      this.flatpickrInstance = null;
      this.categorizedDates = {
        start: [],
        single: [],
        middle: [],
        end: [],
      };

      // Reset tour duration properties
      this.tourDuration = 'single';
      this.showTourDurationToggle = false;

      // Clear all validation errors
      this.validationErrors = {
        peopleUnits: '',
        startEndDate: '',
        personType: '',
        channexComplexChange: '',
      };

      // Force Alpine to re-evaluate template conditions
      Alpine.nextTick(() => {
        this.resetPersonTypeOptions();
      });

      console.log('editBookingItemForm store reset complete');
    },
    toggleFlagColor(color) {
      this.flagColor = this.flagColor === color ? '' : color;
    },
    resetPersonTypeOptions() {
      // Initialize maxPersons safely for current unit type.
      const isPersonUnitType =
        this.unitType === 'Person' ||
        this.unitType === 'Adult' ||
        this.unitType === 'Child';
      if (isPersonUnitType) {
        // Person-type setups often have peoplePerUnit=0; use selected unit count.
        this.maxPersons = Math.max(
          parseInt(this.selectedPeople || 0, 10) || 0,
          1,
        );
      } else {
        this.maxPersons = this.peoplePerUnit || 0;
      }
      this.updatePersonTypeOptions();
    },
    // New method to generate options for adults and children dropdowns
    generatePersonTypeOptions(totalUnits) {
      console.log('FUNCTION CALLED: generatePersonTypeOptions');

      // Check for Person, Adult, or Child unit types
      const isPersonUnitType =
        this.unitType === 'Person' ||
        this.unitType === 'Adult' ||
        this.unitType === 'Child';

      if (isPersonUnitType) {
        // For Person/Adult/Child type, use totalUnits as maxPersons
        this.maxPersons = parseInt(totalUnits) || 1;
        console.log(
          'Person unit type - maxPersons set to totalUnits:',
          this.maxPersons,
        );
      } else {
        // For other types, calculate based on units * peoplePerUnit
        if (this.peoplePerUnit == null || this.units == null) {
          console.warn(
            'peoplePerUnit or units is null, cannot generate options',
          );
          this.adultOptions = [0];
          this.childrenOptions = [0];
          return;
        }

        this.maxPersons = this.peoplePerUnit * this.units;
        console.log('Other type - maxPersons calculated:', {
          peoplePerUnit: this.peoplePerUnit,
          units: this.units,
          maxPersons: this.maxPersons,
        });
      }

      // Generate options from 0 to maxPersons (inclusive)
      this.adultOptions = [...Array(this.maxPersons + 1).keys()];
      this.childrenOptions = [...Array(this.maxPersons + 1).keys()];

      console.log('Generated options:', {
        adultOptions: this.adultOptions,
        childrenOptions: this.childrenOptions,
      });

      // Don't reset default values here since we might have existing selections
      if (!this.selectedAdults && !this.selectedChildren) {
        this.selectedAdults = 0;
        this.selectedChildren = 0;
      }
    },
    updatePersonTypeOptions() {
      console.log('FUNCTION CALLED: updatePersonTypeOptions');

      const maxPersonsRaw = parseInt(this.maxPersons, 10);
      const maxPersons = Number.isNaN(maxPersonsRaw)
        ? 0
        : Math.max(0, maxPersonsRaw);
      const selectedAdultsRaw = parseInt(this.selectedAdults, 10);
      const selectedChildrenRaw = parseInt(this.selectedChildren, 10);
      const selectedAdults = Number.isNaN(selectedAdultsRaw)
        ? 0
        : Math.max(0, selectedAdultsRaw);
      const selectedChildren = Number.isNaN(selectedChildrenRaw)
        ? 0
        : Math.max(0, selectedChildrenRaw);

      // Guard against stale person selections after rate/unit changes.
      // This prevents negative array lengths when a booking item moves to a
      // rate set with lower/zero max person capacity.
      if (maxPersons === 0) {
        this.maxPersons = 0;
        this.selectedAdults = 0;
        this.selectedChildren = 0;
        this.adultOptions = [0];
        this.childrenOptions = [0];
        return;
      }

      // Calculate remaining slots
      const remainingSlots = maxPersons - (selectedAdults + selectedChildren);
      console.log('Remaining slots:', remainingSlots);

      // Update adults options if children changed
      if (this.selectedChildren !== null) {
        const maxAdults = Math.max(0, maxPersons - selectedChildren);
        this.adultOptions = [...Array(maxAdults + 1).keys()];
      }

      // Update children options if adults changed
      if (this.selectedAdults !== null) {
        const maxChildren = Math.max(0, maxPersons - selectedAdults);
        this.childrenOptions = [...Array(maxChildren + 1).keys()];
      }

      // Clamp selected values to newly allowed ranges.
      this.selectedAdults = Math.min(
        selectedAdults,
        this.adultOptions.length - 1,
      );
      this.selectedChildren = Math.min(
        selectedChildren,
        this.childrenOptions.length - 1,
      );

      console.log('Updated options:', {
        adultOptions: this.adultOptions,
        childrenOptions: this.childrenOptions,
        selectedAdults: this.selectedAdults,
        selectedChildren: this.selectedChildren,
        maxPersons: this.maxPersons,
      });
    },
    handlePersonTypeChange() {
      console.log('FUNCTION CALLED: handlePersonTypeChange');

      // Clear person type validation error when user makes changes
      this.clearValidationError('personType');

      // Update both fields' options
      this.updatePersonTypeOptions();

      const totalPeople =
        parseInt(this.selectedAdults || 0) +
        parseInt(this.selectedChildren || 0);

      // Check for Person, Adult, or Child unit types
      const isPersonUnitType =
        this.unitType === 'Person' ||
        this.unitType === 'Adult' ||
        this.unitType === 'Child';

      if (isPersonUnitType) {
        // For Person/Adult/Child unit type, update units with the total
        this.units = totalPeople;
        console.log('Units updated:', {
          adults: this.selectedAdults,
          children: this.selectedChildren,
          totalUnits: this.units,
        });

        // In preserve mode, don't reset dates/price - just update price (which will check preserve mode)
        if (!this.completeReprice) {
          // Preserve mode - just update the warning and keep original price
          this.updatePreserveModeWarning();
          // Price stays as originalPrice - don't call updatePrice() to avoid any flicker
          return;
        }

        // Complete reprice mode - reset dates and recalculate
        this.resetDatesAndPrice();
        if (this.showFixedDateRange === true) {
          // Populate dropdown with options
          this.populateFixedDateRangeDropdown(this.product, this.soldAs);
        } else {
          // Reset Flatpickr dates
          this.resetFlatpickrDates();
          this.updateInitialDateForIdentifier(this.product, this.dateClicked);
        }
        this.updatePrice();
      } else {
        // For other unit types, update selectedPeople with the total
        this.selectedPeople = totalPeople;
        console.log('People updated:', {
          adults: this.selectedAdults,
          children: this.selectedChildren,
          totalPeople: this.selectedPeople,
        });
        if (this.showFixedDateRange === true) {
          // Populate dropdown with options
          this.populateFixedDateRangeDropdown(this.product, this.soldAs);
        }

        // In PRESERVE mode: Don't recalculate price - just show warning if needed
        // In REPRICE mode: Recalculate with new people count
        if (this.completeReprice) {
          this.updatePrice();
        } else {
          // Preserve mode - just update warning, keep original price
          this.updatePreserveModeWarning();
        }
      }

      // customFieldsPerPerson is now an object - getPersonFieldsObj() creates entries on demand
      // No need to pre-initialize when person count changes
    },

    // Method to handle person type dropdown change
    handlePersonTypeDropdownChange() {
      console.log('FUNCTION CALLED: handlePersonTypeDropdownChange');

      // Clear person type validation error when user selects a person type
      this.clearValidationError('personType');
    },

    // Method to handle tour duration change
    handleTourDurationChange() {
      console.log('FUNCTION CALLED: handleTourDurationChange');

      // Reset dates when switching modes - this always requires recalculation
      // because dates are being cleared
      this.startDate = '';
      this.endDate = '';
      // Don't set price to 0 - let the user pick new dates first
      // Price will be calculated when dates are selected

      // Use existing method to clear flatpickr dates
      this.resetFlatpickrDates();

      // Clear the input field visually as well
      if (this.flatpickrInstances[this.product]) {
        const element = this.flatpickrInstances[this.product].element;
        element.value = '';

        // Destroy current instance
        this.flatpickrInstances[this.product].destroy();

        // Reinitialize with new mode, skipping default dates
        this.initializeFlatpickr(this.product, element, true);
      }

      // Show original dates for reference
      this.showOriginalDates();
    },

    // Add or update setValidationError method to persist peopleUnits warning until resolved
    setValidationError(field, message) {
      // Clear any existing timer
      if (this.validationTimer) {
        clearTimeout(this.validationTimer);
        this.validationTimer = null;
      }

      // Set the error
      this.validationErrors = {
        ...this.validationErrors,
        [field]: message,
      };
      // No timer: all warnings persist until validation passes
    },
    clearValidationError(field) {
      this.validationErrors = {
        ...this.validationErrors,
        [field]: '',
      };
    },
  });
}
